import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { AdyenCheckoutError, SubmitActions, SubmitData } from '@adyen/adyen-web';
import { CheckoutContext } from '@/context/CheckoutContext';
import {
  consumeCardActions,
  createCardDetails,
  getAdyenConfig,
  getAutoAdvanceAdyenFieldFocus,
  isAdyenSecuredFieldsIframeLoadError,
  mapAdyenSdkError,
} from '@/utils/adyen';
import { adyenService, createCardFieldHandlers } from '@/services/adyenService';
import type { CardDetails, AdyenCardComponent, CardFieldEvent } from '@/types/adyen';
import { ErrorLogs } from '@/constants/logs';
import { getLogInfoData } from '@/utils/logs';
import { logError } from '@/utils/logger';
import { FeatureFlag } from '@/constants/featureFlags';
import { useReportPaymentMethodUnavailable } from '@/context/PaymentMethodAvailabilityContext';
import { getAppchargeErrorMessage } from '@/utils/error';
import { useTranslation } from 'react-i18next';

export const useAdyen = () => {
  const context = useContext(CheckoutContext);
  const cardRef = useRef<HTMLDivElement>(null);
  const isPaymentInProgress = useRef(false);
  const isInitialized = useRef(false);
  const cardDetailsRef = useRef<CardDetails>(createCardDetails());
  const cardComponentRef = useRef<AdyenCardComponent>();
  const [isReady, setIsReady] = useState(false);
  const [isAdyenFieldsValid, setIsAdyenFieldsValid] = useState(false);
  const activeFieldRef = useRef<string | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const setUnavailable = useReportPaymentMethodUnavailable();
  const { t } = useTranslation();

  const {
    customer,
    paymentMethodsSettings,
    paymentMethodSelected,
    featureFlags,
    actions,
    card: { setCardElementRef, adyenCardDetailsRef },
  } = context;
  const showCardholderName = Boolean(featureFlags?.[FeatureFlag.CHECKOUT_SHOW_ADYEN_CARDHOLDER_NAME_FIELD]);

  const handleError = useCallback(
    (err?: AdyenCheckoutError) => {
      if (isAdyenSecuredFieldsIframeLoadError(err)) {
        setUnavailable?.(true);
        return;
      }
      isPaymentInProgress.current = false;
      const mappedError = mapAdyenSdkError(err?.name);
      if (mappedError === null) return;
      actions.setError(getAppchargeErrorMessage(t, mappedError));
    },
    [actions, setUnavailable, t]
  );

  const initializeAdyen = useCallback(async () => {
    if (isInitialized.current || !cardRef.current) return;

    const adyenConfig = getAdyenConfig(paymentMethodsSettings as unknown[], paymentMethodSelected);

    if (!adyenConfig) return;

    try {
      isInitialized.current = true;
      await adyenService.initialize(adyenConfig, customer);

      const baseFieldHandlers = createCardFieldHandlers(cardDetailsRef, cardComponentRef, handleError);

      const handlers = {
        ...baseFieldHandlers,
        onFieldValid: (event: CardFieldEvent) => {
          baseFieldHandlers.onFieldValid(event);
          getAutoAdvanceAdyenFieldFocus(event, cardComponentRef.current);
        },
        onChange: (state: { isValid: boolean }) => {
          setIsAdyenFieldsValid(state.isValid);
        },
        onSubmit: (state: SubmitData, _: unknown, actions: SubmitActions) => {
          cardDetailsRef.current = {
            ...cardDetailsRef.current,
            paymentMethodData: state.data.paymentMethod as Record<string, unknown>,
            browserInfo: state.data.browserInfo as unknown as Record<string, unknown>,
          };
          adyenCardDetailsRef.current = cardDetailsRef.current;
          adyenCardDetailsRef.current.actions = actions;
          // Reject through the ref rather than the captured `actions` so the
          // handle is cleared as it is spent; a leftover one would later be
          // mistaken for a live promise and swallow that payment's outcome.
          adyenService.setOnPaymentFailed(() => consumeCardActions(adyenCardDetailsRef)?.reject());
        },
        onFocus: (event: { fieldType: string }) => {
          // Clear any pending blur timeout to keep keyboard open
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
          }
          activeFieldRef.current = event.fieldType;
        },
        onBlur: () => {
          // Delay clearing active field to allow focus transition to next field
          blurTimeoutRef.current = setTimeout(() => {
            activeFieldRef.current = null;
          }, 100);
        },
        onConfigSuccess: () => {
          isPaymentInProgress.current = false;
          setIsReady(true);
        },
      };
      adyenService
        .createCardComponent(cardRef.current, handlers, {
          hasHolderName: showCardholderName,
          holderNameRequired: showCardholderName,
        })
        .then((component) => {
          if (component) {
            cardComponentRef.current = component;
            setCardElementRef({ current: cardComponentRef.current });
          }
        });
    } catch {
      logError(ErrorLogs.adyenCardFormError, getLogInfoData(context));
      isInitialized.current = false;
      setUnavailable?.(true);
    }
  }, [
    paymentMethodsSettings,
    paymentMethodSelected,
    customer,
    handleError,
    actions,
    setCardElementRef,
    adyenCardDetailsRef,
    showCardholderName,
  ]);

  useEffect(() => {
    initializeAdyen();
  }, [initializeAdyen, adyenCardDetailsRef]);

  return {
    cardRef,
    isReady,
    isAdyenFieldsValid,
  };
};
